import { useState } from "react";
import useDataset from "datasets/hooks/useDataset";
import AgentInstructionsEditor from "datasets/pages/settings/components/AgentInstructionsEditor";
import OverviewTab from "datasets/pages/settings/components/OverviewTab";
import useInitializationStatus from "datasets/pages/settings/hooks/useInitializationStatus";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { ReadOnlyBadge } from "~/components/ReadOnlyBadge";
import useErrorToast from "~/contexts/error-toast";
import { apiMutate } from "~/utils/apiClient";
import rumClient from "~/utils/monitoring";

const { VITE_CATALOG_API, VITE_ANALYSIS_AGENT_API } = import.meta.env;

const formatDate = (dateStr: string | undefined) => {
  if (!dateStr) {
    return "";
  }

  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
};

interface DatasetAIContextProps {
  // Overrides the route param — set when rendered outside the DatasetDetail
  // route tree (the project session context sidebar), which has no :datasetId.
  datasetId?: string;
  // True when rendered inside the session context sidebar, which supplies its
  // own header, so the redundant page title is suppressed.
  sessionSidebarView?: boolean;
}

export const DatasetAIContext = ({
  datasetId: datasetIdProp,
  sessionSidebarView = false,
}: DatasetAIContextProps = {}) => {
  const { data: dataset } = useDataset(datasetIdProp);
  const { showError } = useErrorToast();
  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;
  // The sidebar is a read-only detail view — editing happens on the full dataset
  // page, which its header links out to.
  const canEdit = isOwner && !sessionSidebarView;
  const isEnabled = dataset?.aiAgentsEnabled === true;
  const [isInitializing, setIsInitializing] = useState(false);
  const [isReinitializing, setIsReinitializing] = useState(false);

  const {
    data: initStatus,
    mutate: refreshInitStatus,
    isLoading: initStatusLoading,
  } = useInitializationStatus(dataset?.id, isEnabled);

  const triggerInitialization = async () => {
    if (!dataset) {
      return;
    }

    setIsInitializing(true);
    try {
      await apiMutate(
        `${VITE_ANALYSIS_AGENT_API}/datasets/${dataset.id}/initialization`,
        "POST",
        {}
      );
      refreshInitStatus();
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError("Unable to start dataset initialization");
    } finally {
      setIsInitializing(false);
    }
  };

  const triggerReinitialization = async () => {
    if (!dataset) {
      return;
    }

    setIsReinitializing(true);
    try {
      await apiMutate(
        `${VITE_CATALOG_API}/datasets/${dataset.id}/agent-metadata`,
        "DELETE",
        undefined,
        { expects204: true }
      );
      await apiMutate(
        `${VITE_ANALYSIS_AGENT_API}/datasets/${dataset.id}/initialization`,
        "POST",
        {}
      );
      refreshInitStatus();
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError("Unable to reinitialize dataset");
    } finally {
      setIsReinitializing(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
      {!sessionSidebarView && <h1 className="text-2xl font-semibold">Context</h1>}

      <section className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-medium text-foreground">Instructions</h2>
          {!canEdit && <ReadOnlyBadge />}
        </div>
        <AgentInstructionsEditor datasetId={datasetIdProp} readOnly={sessionSidebarView} />
      </section>

      <section className="flex flex-col gap-1">
        <h2 className="text-base font-medium text-foreground">Overview</h2>
        <OverviewTab
          datasetId={datasetIdProp}
          status={initStatus?.status}
          statusLoading={initStatusLoading}
          generatedAt={formatDate(initStatus?.generatedAt)}
          error={initStatus?.error}
          phase={initStatus?.phase}
          progress={initStatus?.progress}
          onInitialize={triggerInitialization}
          onReinitialize={triggerReinitialization}
          isInitializing={isInitializing}
          isReinitializing={isReinitializing}
          isOwner={isOwner}
          readOnly={sessionSidebarView}
        />
      </section>
    </div>
  );
};
